import { Divider } from "@heroui/react";
import FilterVesselCharacteristicsSelection from "./Filter-Vessel-Characteristics-Selection";
import FilterOwnershipsSelection from "./Filter-Ownerships-Selection";
import SelectionSave from "../components/filter-detail/Selection-Save";
import SelectionApply from "../components/filter-detail/Selection-Apply";
import { useAppDispatch, useAppSelector } from "../../../core/rootHooks";
import { selectEmptyFilter, selectFilterNames, selectIsFilterBusy, selectSelectedFilter } from "../filterSelectors";
import { IFilterForm } from "../filterTypes";
import { SubmitHandler, useForm } from "react-hook-form";
import { AppDispatch } from "../../../core/store";
import ModalManager from "../../../shared/components/modal/ModalManager";
import { FilterWrapper } from "../types/classes/FilterWrapper";
import { addFilterRequest, addDraftFilterRequest, updateDraftFilterRequest, setSelectedFilterRequest, updateFilterRequest } from "../filterSlice";
import SharedSpinner from "../../../shared/components/spinner/SharedSpinner";

import { sortBy, uniqueId, cloneDeep, isEqual } from "lodash";
import { selectSubscribedMsiOwnerships, selectSubscribedMsiVessels, selectUserId } from "../../../user-management/appSelectors";
import { setSelectedVesselsForOutputRequest } from "../../search/searchSlice";
import { EOutputType, ESelectionType } from "../../../shared/sharedEnums";
import { IFilterFormData } from "../../../shared/sharedTypes";
import { useRef } from "react";
interface IFilterDetailPanelProps {
    closeFilterDetail: () => void;
    displayFilterMainPanel: (value: string) => void;
}

const FilterDetailPanel: React.FC<IFilterDetailPanelProps> = ({ ...props }) => {
    const dispatch = useAppDispatch<AppDispatch>();

    const subscribedMsiOwnerships: string[] = useAppSelector(selectSubscribedMsiOwnerships);
    const selectedFilter: IFilterForm = useAppSelector(selectSelectedFilter);
    const subscribedMsiVessels = useAppSelector(selectSubscribedMsiVessels);
    const filterNames: string[] = useAppSelector(selectFilterNames);

    const emptyFilter: FilterWrapper = useAppSelector(selectEmptyFilter);
    const isBusy: boolean = useAppSelector(selectIsFilterBusy);
    const currentUserId: number | undefined = useAppSelector(selectUserId);

    const initialValue = useRef<IFilterForm>(cloneDeep(selectedFilter) ?? cloneDeep(emptyFilter));

    // Form hook to keep track of the form values and errors
    const { register, handleSubmit, reset, getValues, trigger, formState: { errors } } = useForm({
        mode: 'onSubmit',
        defaultValues: {
            name: selectedFilter?.name || '',
            scope: selectedFilter?.scope || '',
            selectedSectorFilters: selectedFilter.filters.sectorFilter,
            selectedOwnershipFilters: selectedFilter.filters.ownershipFilter,
        }
    });

    const onSaveSubmit: SubmitHandler<any> = (data) => {
        // if scope is team, set teamId to user's teamId otherwise set it to null
        if (selectedFilter?.id) {
            // update filter
            const filter = {
                id: selectedFilter.id, name: data.name, scope: data.scope,
                sectorFilter: JSON.stringify(data.selectedSectorFilters.sectors),
                ownershipFilter: JSON.stringify(data.selectedOwnershipFilters.ownerships),
                isOwnFilter: selectedFilter.isOwnFilter,
                isDraft: false
            };

            const isChanged = !isEqual(sortBy(data.selectedSectorFilters.sectors), sortBy(initialValue.current.filters.sectorFilter.sectors)) ||
                !isEqual(sortBy(data.selectedOwnershipFilters.ownerships), sortBy(initialValue.current.filters.ownershipFilter.ownerships)) ||
                !isEqual(data.name, initialValue.current.name) ||
                !isEqual(data.scope, initialValue.current.scope);

            if (!isChanged && !selectedFilter.isDraft) {
                ModalManager.show({ header: "Warning", message: "No changes found in Filter.", showActionButton: false, size: "xs" });
                return;
            }

            initialValue.current = cloneDeep(selectedFilter);
            dispatch(updateFilterRequest({ filter }));
        }
        else {
            // add filter
            const filter = {
                id: null, name: data.name, scope: data.scope,
                sectorFilter: JSON.stringify(data.selectedSectorFilters.sectors),
                ownershipFilter: JSON.stringify(data.selectedOwnershipFilters.ownerships),
                isDraft: false,
                tempId: selectedFilter?.tempId
            };
            dispatch(addFilterRequest({ filter }));
        }
    };

    const onDisplayFilterOutputSubmit: SubmitHandler<any> = (data) => {
        
        if (!trigger()) {
            return;
        }

        if (subscribedMsiVessels === null || subscribedMsiVessels === undefined || subscribedMsiVessels?.length === 0) {
            ModalManager.show({ header: "Warning", message: "No subscribed vessels found. Please contact MSI.", showActionButton: false, size: "sm" });
            return;
        }
        
        const filteredSubscribedVessels = FilterWrapper.applyFilters(subscribedMsiVessels, selectedFilter.filters.ownershipFilter.ownerships, selectedFilter.filters.sectorFilter.sectors);

        if (filteredSubscribedVessels.length === 0) {
            ModalManager.show({ header: "Warning", message: "No vessels found in Filter. Please change your filter or contact MSI.", showActionButton: false, size: "sm" });
            return;
        }
        let tempId = undefined;
        let selectionType = ESelectionType.Filter;
        if (selectedFilter?.id || (selectedFilter?.id === null && selectedFilter?.tempId && selectedFilter?.isDraft === true)) {
            // update filter on state only as draft
            const filter = {
                id: selectedFilter.id, name: data.name, scope: data.scope,
                sectorFilter: JSON.stringify(data.selectedSectorFilters.sectors),
                ownershipFilter: JSON.stringify(data.selectedOwnershipFilters.ownerships),
                isDraft: true,
                tempId: selectedFilter.tempId
            };

            const isChanged = !isEqual(sortBy(data.selectedSectorFilters.sectors), sortBy(initialValue.current.filters.sectorFilter.sectors)) ||
                !isEqual(sortBy(data.selectedOwnershipFilters.ownerships), sortBy(initialValue.current.filters.ownershipFilter.ownerships)) ||
                !isEqual(data.name, initialValue.current.name) ||
                !isEqual(data.scope, initialValue.current.scope);

            if (isChanged) {
                dispatch(updateDraftFilterRequest({ filter }));
                selectionType = ESelectionType.DraftFilter;
            }

        }
        else {
            // add draft filter - no save -submit
            tempId = uniqueId('temp-');
            const filter = {
                id: null, name: data.name, scope: data.scope,
                sectorFilter: JSON.stringify(data.selectedSectorFilters.sectors),
                ownershipFilter: JSON.stringify(data.selectedOwnershipFilters.ownerships),
                createdById: currentUserId,
                isOwnFilter: true,
                isDraft: true,
                tempId: tempId
            };
            dispatch(addDraftFilterRequest({ filter }));
            selectionType = ESelectionType.DraftFilter;
        }

        dispatch(setSelectedVesselsForOutputRequest({
            outputSelection: {
                selectedVesselsList: filteredSubscribedVessels,
                selectionName: data.name, selectionType: selectedFilter.id === null || selectedFilter.isDraft ? ESelectionType.DraftFilter : selectionType,
                outputType: filteredSubscribedVessels.length === 1 ? EOutputType.Vessel : filteredSubscribedVessels.length > 200 ? EOutputType.Summary : EOutputType.Fleet,
                selectionTypeObject: {
                    id: selectedFilter.id || undefined, name: data.name,
                    scope: data.scope, isOwnFilter: selectedFilter.isOwnFilter, isDraft: selectedFilter.isDraft, tempId: tempId
                } as IFilterFormData
            }
        }));



        props.closeFilterDetail();
        props.displayFilterMainPanel('');
    }

    const discardChanges = () => {
        // const initialFilterOwnerships = props.filter?.filterOwnerships ?? [];
        // setSelectedKeys(new Set(initialFilterVessels));

        reset();
        if (selectedFilter.id === null) {
            selectedFilter.filters = emptyFilter;
            props.closeFilterDetail();

        }
        else {
            dispatch(setSelectedFilterRequest({ id: selectedFilter.id }));

            // setSelectedScope(selectedFilter?.scope || '');
            // setFilterName(selectedFilter?.name || '');
        }
    }

    return (

        <div className="flex flex-row h-full">
            <div className='flex-1'><Divider className=" my-0 bg-gray-200 " orientation="vertical" /></div>
            <div className="flex flex-col">
                <div className="flex flex-auto h-full overflow-y-hidden bg-msi-white-color" >
                    <FilterVesselCharacteristicsSelection sectorFilter={selectedFilter.filters.sectorFilter} />
                    <Divider className="mx-2 my-3 bg-gray-200 " orientation="vertical" />
                    <FilterOwnershipsSelection handleCloseFilterDetailOnClick={props.closeFilterDetail}
                        msiOwnerships={subscribedMsiOwnerships} ownershipFilter={selectedFilter.filters.ownershipFilter} />
                </div>

                <form className="flex-1">
                    <div className="flex-initial flex flex-row items-start space-x-2 bg-msi-white-color px-2 py-4 ">
                        <SelectionSave onSubmit={handleSubmit(onSaveSubmit)} isEditAllowed={selectedFilter?.isOwnFilter ?? true} register={register}
                            errors={errors} label="Save Filter:"
                            selectedOption={selectedFilter.scope} name={selectedFilter.name} filterNames={filterNames} />
                        <input type="hidden" {...register('selectedSectorFilters', {
                            validate: {
                                atLeastOneFilter: value => (value.isAtLeastOneSectorFilterActive() ||
                                    (!value.isAtLeastOneSectorFilterActive() && getValues("selectedOwnershipFilters").isAtLeastOneOwnershipFilterActive()))
                                    || 'At least one sector filter or ownership selection is required',
                                filterSelectionMustHave: value => (!value.checkIfAnyFilterSelectionIsEmpty())
                                    || 'At least one sector characteristic must be selected if \'Filter\' is selected'
                            }

                        })} />
                        <input type="hidden" {...register('selectedOwnershipFilters', {
                            validate: {
                                atLeastOneFilterOrAll: () => (getValues("selectedSectorFilters").isAtLeastOneSectorFilterActive())
                                    || 'At least one sector filter must be different from \'None\'',
                            }
                        })} />

                    </div>
                    {errors.selectedSectorFilters && <span className="w-auto mx-2 mt-1 text-red-500 text-xs">{errors.selectedSectorFilters.message}</span>}
                    {errors.selectedOwnershipFilters && <span className="w-auto mx-2 mt-1 text-red-500 text-xs">{errors.selectedOwnershipFilters.message}</span>}
                    <Divider className='w-auto mx-2 mt-1 bg-gray-200' />
                    <div className="flex-initial flex py-4 px-2 justify-end space-x-4 bg-msi-white-color">
                        <SelectionApply displayOutput={handleSubmit(onDisplayFilterOutputSubmit)} discardChanges={discardChanges} />
                    </div>
                </form>
            </div>

            <SharedSpinner size="lg" textSize="text-medium" isBusy={isBusy} label="Saving filter..." />
        </div>
    )

}

export default FilterDetailPanel;


